import React from "react";
import { motion } from "framer-motion";
import { FaHotel, FaRobot, FaBoxes, FaSmile } from "react-icons/fa";

const AboutUs = () => {
  const services = [
    {
      icon: <FaHotel />,
      title: "Hotel Management",
      description: "Comprehensive hotel management solutions",
    },
    {
      icon: <FaRobot />,
      title: "Staff Automation",
      description: "Advanced staff management systems",
    },
    {
      icon: <FaBoxes />,
      title: "Inventory Tracking",
      description: "Real-time inventory management",
    },
    {
      icon: <FaSmile />,
      title: "Guest Experience",
      description: "Enhanced guest satisfaction solutions",
    },
  ];

  const whyChooseUs = [
    {
      title: "Unmatched Quality",
      description:
        "We provide top-notch hospitality solutions that exceed expectations.",
    },
    {
      title: "24/7 Support",
      description:
        "Our dedicated team ensures round-the-clock assistance for clients.",
    },
    {
      title: "Trusted by Industry Leaders",
      description:
        "Our solutions are trusted by top hotels and restaurants worldwide.",
    },

    {
      title: "Innovative Solutions",
      description:
        "We leverage cutting-edge technology to keep you ahead in the industry.",
    },
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Hero Section - Enhanced & Elegant */}
      <div
        className="relative bg-cover bg-center"
        style={{
          height: "55vh",
          minHeight: "450px",
          maxHeight: "550px",
          backgroundImage:
            "url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb')",
          backgroundPosition: "center center",
          backgroundSize: "cover",
        }}
      >
        {/* Elegant overlay with better transparency */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(238, 46, 36, 0.75) 0%, rgba(46, 46, 46, 0.70) 50%, rgba(0, 0, 0, 0.65) 100%)",
          }}
        ></div>

        {/* Decorative top accent */}
        <div
          className="absolute top-0 left-0 right-0"
          style={{
            height: "4px",
            background:
              "linear-gradient(90deg, #EE2E24 0%, #FFC400 50%, #EE2E24 100%)",
          }}
        ></div>

        {/* Content container */}
        <div className="container mx-auto h-full flex flex-col items-center justify-center text-center px-4 relative z-10">
          {/* Decorative element above title */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{
              width: "60px",
              height: "4px",
              background: "linear-gradient(90deg, #EE2E24 0%, #FFC400 100%)",
              borderRadius: "2px",
              marginBottom: "20px",
            }}
          ></motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              fontSize: "clamp(2.2rem, 5.5vw, 3.8rem)",
              fontWeight: "800",
              color: "#FFFFFF",
              textShadow:
                "0 4px 20px rgba(0,0,0,0.4), 0 2px 8px rgba(238, 46, 36, 0.3)",
              marginBottom: "16px",
              letterSpacing: "-1.5px",
              lineHeight: "1.1",
            }}
          >
            Trivana Hospitality
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "80px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{
              height: "3px",
              background:
                "linear-gradient(90deg, transparent 0%, #FFC400 50%, transparent 100%)",
              borderRadius: "2px",
              marginBottom: "18px",
            }}
          ></motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            style={{
              fontSize: "clamp(1rem, 2vw, 1.25rem)",
              fontWeight: "500",
              color: "#FFFFFF",
              marginBottom: "10px",
              letterSpacing: "-0.3px",
              maxWidth: "700px",
              lineHeight: "1.6",
              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
            }}
          >
            Redefining Excellence in Hospitality Management
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            style={{
              fontSize: "clamp(0.9rem, 1.5vw, 1.05rem)",
              fontWeight: "400",
              color: "rgba(255, 255, 255, 0.95)",
              marginBottom: "28px",
              letterSpacing: "-0.1px",
              maxWidth: "650px",
              lineHeight: "1.7",
              textShadow: "0 1px 8px rgba(0,0,0,0.3)",
            }}
          >
            Premium hotels • Fine dining • Unforgettable experiences
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            whileHover={{
              scale: 1.05,
              boxShadow: "0 8px 25px rgba(255, 196, 0, 0.4)",
            }}
            whileTap={{ scale: 0.98 }}
            style={{
              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
              color: "#FFFFFF",
              padding: "13px 32px",
              borderRadius: "50px",
              fontWeight: "700",
              fontSize: "0.95rem",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(238, 46, 36, 0.35)",
              letterSpacing: "-0.15px",
              transition: "all 0.3s ease",
            }}
            onClick={() =>
              window.open("https://trivanahospitality.com", "_blank")
            }
          >
            Discover Our Story
          </motion.button>

          {/* Decorative element below button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            style={{
              marginTop: "25px",
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "30px",
                height: "2px",
                background: "rgba(255, 196, 0, 0.6)",
                borderRadius: "1px",
              }}
            ></div>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#FFC400",
                boxShadow: "0 0 10px rgba(255, 196, 0, 0.5)",
              }}
            ></div>
            <div
              style={{
                width: "30px",
                height: "2px",
                background: "rgba(255, 196, 0, 0.6)",
                borderRadius: "1px",
              }}
            ></div>
          </motion.div>
        </div>

        {/* Decorative bottom accent */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: "4px",
            background:
              "linear-gradient(90deg, #EE2E24 0%, #FFC400 50%, #EE2E24 100%)",
            opacity: 0.8,
          }}
        ></div>
      </div>

      {/* Our Story Section - Compact */}
      <div
        id="our-story"
        style={{
          padding: "50px 0",
          background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        }}
      >
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-8 items-center">
          <motion.div
            className="relative rounded-xl overflow-hidden"
            style={{
              height: "320px",
              boxShadow: "0 6px 25px rgba(238, 46, 36, 0.1)",
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa"
              alt="Our Story"
              className="w-full h-full object-cover transform transition-transform duration-700 hover:scale-105"
            />
            <div
              className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300"
              style={{
                border: "3px solid #EE2E24",
              }}
            ></div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <h2
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: "800",
                color: "#2E2E2E",
                marginBottom: "14px",
                letterSpacing: "-0.6px",
              }}
            >
              Our Story
            </h2>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#2E2E2E",
                lineHeight: "1.65",
                marginBottom: "14px",
                letterSpacing: "-0.15px",
              }}
            >
              Since our inception,{" "}
              <span style={{ fontWeight: "700", color: "#EE2E24" }}>
                Trivana Hospitality
              </span>{" "}
              has been dedicated to transforming the hospitality industry
              through innovative solutions and unparalleled service excellence.
            </p>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#2E2E2E",
                lineHeight: "1.65",
                letterSpacing: "-0.15px",
              }}
            >
              Our journey began with a vision to revolutionize how hotels
              operate, and today we proudly stand as a trusted leader in
              hospitality management solutions worldwide.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Services Section - Compact */}
      <div
        style={{
          padding: "50px 0",
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: "800",
              color: "#FFFFFF",
              textAlign: "center",
              marginBottom: "32px",
              letterSpacing: "-0.6px",
            }}
          >
            Our Services
          </h2>
          <div className="grid md:grid-cols-4 gap-5">
            {services.map((service, index) => (
              <motion.div
                key={index}
                whileHover={{
                  y: -8,
                  scale: 1.03,
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.18)",
                }}
                transition={{ duration: 0.25 }}
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  padding: "20px",
                  borderRadius: "12px",
                  textAlign: "center",
                  boxShadow: "0 3px 12px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div
                  style={{
                    fontSize: "2.2rem",
                    color: "#FFFFFF",
                    marginBottom: "12px",
                    filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.15))",
                  }}
                >
                  {service.icon}
                </div>
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: "700",
                    color: "#FFFFFF",
                    marginBottom: "6px",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {service.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "rgba(255, 255, 255, 0.95)",
                    lineHeight: "1.45",
                    letterSpacing: "-0.15px",
                  }}
                >
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us Section - Compact */}
      <div
        style={{
          padding: "50px 0",
          background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: "800",
              color: "#2E2E2E",
              textAlign: "center",
              marginBottom: "32px",
              letterSpacing: "-0.6px",
            }}
          >
            Why Choose Us
          </h2>
          <div className="grid md:grid-cols-4 gap-5">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                whileHover={{
                  scale: 1.03,
                  boxShadow: "0 10px 30px rgba(238, 46, 36, 0.12)",
                }}
                style={{
                  padding: "20px",
                  background: "#FFFFFF",
                  borderRadius: "12px",
                  boxShadow: "0 3px 12px rgba(0, 0, 0, 0.07)",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                  transition: "all 0.25s ease",
                }}
              >
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: "700",
                    marginBottom: "8px",
                    color: "#EE2E24",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "#2E2E2E",
                    lineHeight: "1.55",
                    letterSpacing: "-0.15px",
                  }}
                >
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AboutUs;
